import { Check, X } from 'lucide-react';
import type { AnchorType } from '../types';

interface EditPathPanelProps {
  /** Index of the selected anchor in the working path, or null
   *  when no anchor is selected. Drives the type-radio's enabled
   *  state + display value. */
  selectedAnchorIndex: number | null;
  /** Current type of the selected anchor — sourced from the
   *  canvas-side working path via `getPathEditSelectedAnchorType`.
   *  Re-read on every render via the parent's selection-change
   *  callback. Null when no anchor is selected. */
  selectedAnchorType: AnchorType | null;
  /** Set the type of the currently-selected anchor. */
  onAnchorTypeChange: (type: AnchorType) => void;
  /** Apply — commits the working path to the layer + closes the
   *  session. */
  onApply: () => void;
  /** Cancel — restores the snapshot + closes the session. */
  onCancel: () => void;
}

const TYPE_OPTIONS: { value: AnchorType; label: string; description: string }[] = [
  {
    value: 'smooth',
    label: 'Smooth',
    description: 'Handles collinear + equal length',
  },
  {
    value: 'corner',
    label: 'Corner',
    description: 'Independent handles, sharp transition',
  },
  {
    value: 'asymmetric',
    label: 'Asymmetric',
    description: 'Collinear handles, different lengths',
  },
];

/** Properties-panel content during Path Edit mode — Tier 2
 *  Group A PR-B. Click an anchor in the canvas to select it,
 *  then pick its type here. Cancel/Apply close the session.
 *
 *  The numeric X/Y inputs + Add/Delete anchor buttons from the
 *  roadmap spec are deferred to a follow-up; this MVP covers
 *  click-select + drag (canvas-side) + type cycling (panel-side).
 *  The on-canvas mouse handlers in `useFabricCanvas` are the
 *  primary editing surface; this panel exists for the discrete
 *  per-anchor decisions (type) that don't fit a drag gesture. */
export function EditPathPanel({
  selectedAnchorIndex,
  selectedAnchorType,
  onAnchorTypeChange,
  onApply,
  onCancel,
}: EditPathPanelProps) {
  const hasSelection = selectedAnchorIndex !== null;

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Edit Path</h4>

      <div className="text-xs text-muted-foreground leading-relaxed">
        {hasSelection
          ? `Anchor ${selectedAnchorIndex + 1} selected. Drag the anchor or its handles in the canvas to reshape; pick a type below.`
          : 'Click an anchor in the canvas to select it. Drag handles to bend the curve. Click empty canvas to deselect.'}
      </div>

      <section className="flex flex-col gap-1">
        <h5 className="text-[10px] font-medium text-muted-foreground uppercase">Anchor type</h5>
        <div className="flex flex-col gap-1">
          {TYPE_OPTIONS.map((opt) => {
            const isActive = hasSelection && selectedAnchorType === opt.value;
            return (
              <label
                key={opt.value}
                className={`flex items-start gap-2 text-xs cursor-pointer rounded p-1.5 ${
                  isActive ? 'bg-primary/10 border border-primary/40' : 'border border-transparent'
                } ${hasSelection ? '' : 'opacity-50 pointer-events-none'}`}
              >
                <input
                  type="radio"
                  name="path-edit-anchor-type"
                  value={opt.value}
                  checked={isActive}
                  onChange={() => onAnchorTypeChange(opt.value)}
                  className="mt-0.5"
                  disabled={!hasSelection}
                />
                <div className="flex flex-col">
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-[10px] text-muted-foreground">{opt.description}</span>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      <div className="flex items-center gap-2 mt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-3 py-1.5 rounded border border-border text-xs hover:bg-accent inline-flex items-center justify-center gap-1"
          title="Cancel — discard edits"
        >
          <X className="h-3.5 w-3.5" />
          Cancel
        </button>
        <button
          type="button"
          onClick={onApply}
          className="flex-1 px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs hover:opacity-90 inline-flex items-center justify-center gap-1"
          title="Apply — commit edits to the layer"
        >
          <Check className="h-3.5 w-3.5" />
          Apply
        </button>
      </div>
    </div>
  );
}
